import React from 'react';
import { AppBar, Toolbar, Box, IconButton, useMediaQuery, useTheme, Menu, MenuItem } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import cfcLogo from '../Assets/imgs/cfc-logo.png';
import agriIntelLogo from '../Assets/imgs/agri-intel-logo.png';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Tooltip from '@mui/material/Tooltip';
import '../Assets/css/Navbar.css';
import Avatar from '@mui/material/Avatar';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../contexts/AuthContext';


export const stringAvatar = (name) => {
  if (!name || typeof name !== 'string') return { sx: {}, children: '' };
  const nameParts = name.split(' ');
  const initials = nameParts.map(part => part[0] ? part[0].toUpperCase() : '').join('').slice(0, 2); // Get first 2 initials
  return {
    sx: {
      bgcolor: '#4D87C1', // Fixed background color
      color: 'white',     // White font color
      width: 32,
      height: 32,
      marginRight: 1,
      fontFamily: 'Kumbh Sans',
      fontSize: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    children: initials,
  };
};


const Navbar = () => {
  const { handleLogout, isAuthenticated, username, role } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);

  const getLogoSize = () => {
    if (isXsScreen) return { width: '80px', height: '32px' };
    if (isSmScreen) return { width: '100px', height: '40px' };
    return { width: '120px', height: '50px' };
  };

  const AGRI_LOGO_ASPECT_RATIO = 1123 / 373; 

  const getAgriLogoSize = () => {
    let height;
    if (isXsScreen) height = 26;
    else if (isSmScreen) height = 34;
    else height = 52;
    return { height: `${height}px`, width: `${Math.round(height * AGRI_LOGO_ASPECT_RATIO)}px` };
  };

  const theme1 = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "12px",
            fontFamily: "Kumbh Sans, sans-serif",
          },
        },
      },
    },
  });
  

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    handleLogout();
  };
  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/admin');
  };
  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      sx={{
        zIndex: 9999, // Extremely high z-index to ensure it's on top
        '& .MuiPaper-root': {
          zIndex: 9999, // Ensure the menu's paper component is also on top
          position: 'fixed', // Use fixed positioning to take it out of normal document flow
          mt: 1, ml: 1.5
        }
      }}
      PaperProps={{
        style: {
          zIndex: 9999, // Additional z-index on PaperProps
        }
      }}
      PopoverClasses={{
        root: 'navbar-menu-root', // Add a custom class for additional styling if needed
      }}
    >
      <MenuItem sx={{ justifyContent: 'flex-start', pointerEvents: "none" }} >
        <strong style={{ fontSize: '15px', fontFamily: 'Kumbh sans' }}>{username}</strong>
      </MenuItem>
      {role === 'admin' && (
        <MenuItem onClick={handleSettingsClick} sx={{ justifyContent: 'flex-start' }}>
          <SettingsIcon sx={{ mr: 1, fontSize: '1rem' }} />
          <strong style={{ fontSize: '15px', fontFamily: 'Kumbh sans' }}>Dashboard</strong>
        </MenuItem>
      )}
      <MenuItem onClick={handleLogoutClick} sx={{ justifyContent: 'flex-start' }}>
        <LogoutIcon sx={{ mr: 1, fontSize: '1rem' }} />
        <strong style={{ fontSize: '15px', fontFamily: 'Kumbh sans' }}>Logout</strong>
      </MenuItem>
    </Menu>
  );


  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(mobileMoreAnchorEl)}
      onClose={handleMobileMenuClose}
      sx={{
        zIndex: 9999, // Extremely high z-index to ensure it's on top
        '& .MuiPaper-root': {
          zIndex: 9999, // Ensure the menu's paper component is also on top
          position: 'fixed', // Use fixed positioning to take it out of normal document flow
          mt: 1.5, ml: 1.5
        }
      }}
      PaperProps={{
        style: {
          zIndex: 9999, // Additional z-index on PaperProps
        }
      }}
      PopoverClasses={{
        root: 'navbar-menu-root', // Add a custom class for additional styling if needed
      }}
    >
      <MenuItem sx={{ justifyContent: 'flex-start', padding: '4px 8px', minHeight: '30px', pointerEvents: "none" }}>
        <strong style={{ fontSize: '12px', fontFamily: 'Kumbh sans' }}>{username}</strong>
      </MenuItem>
      {role === 'admin' && (
        <MenuItem onClick={handleSettingsClick} sx={{ justifyContent: 'flex-start', padding: '4px 8px', minHeight: '30px' }}>
          <SettingsIcon sx={{ mr: 1, fontSize: '1rem' }} />
          <strong style={{ fontSize: '12px', fontFamily: 'Kumbh sans' }}>Dashboard</strong>
        </MenuItem>
      )}
      <MenuItem onClick={handleLogoutClick} sx={{ justifyContent: 'flex-start', padding: '4px 8px', minHeight: '30px' }}>
        <LogoutIcon sx={{ mr: 1, fontSize: '1rem' }} />
        <strong style={{ fontSize: '12px', fontFamily: 'Kumbh sans' }}>Logout</strong>
      </MenuItem>
    </Menu>
  );


  const handleLogoClick = () => {
    
    if (localStorage.getItem('model-selected') === 'model3') {

          localStorage.removeItem('selectedAssociations');
            localStorage.removeItem('selectedYears');
            localStorage.removeItem('selectedMetrics')
      navigate('/ask-questions');

    } else if (localStorage.getItem('model-selected') === 'model1') {
      localStorage.removeItem('selectedAssociations');
      localStorage.removeItem('selectedYears');
      localStorage.removeItem('selectedMetrics')
      navigate('/home');
    } else if (localStorage.getItem('model-selected') === 'model2'){
      localStorage.removeItem('selectedAssociations');
      localStorage.removeItem('selectedYears');
      localStorage.removeItem('selectedMetrics')
      navigate('/salescoach'); 
    } else {
      localStorage.removeItem('selectedAssociations');
      localStorage.removeItem('selectedYears');
      localStorage.removeItem('selectedMetrics')
      navigate('/sage'); // Default
    }
  };

  return (
    <AppBar className='appbar-css' component="nav" role="navigation" aria-label="Main navigation">
      <Toolbar>
        <Box >
          <button
            type="button"
            onClick={handleLogoClick}
            aria-label="Go to home page"
            style={{ border: 0, padding: 0, background: 'transparent', cursor: 'pointer' }}
          >
          <img
            src={cfcLogo}
            className="cfclogo-css"
            alt="Capital Farm Credit Logo"
            role="img"
            style={{
              ...getLogoSize(),
              cursor: "pointer"
            }}
          />
          </button>

        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
          role="heading"
          aria-level="1"
        >
          <img
            src={agriIntelLogo}
            alt="Agri-Intel"
            style={{
              ...getAgriLogoSize(),
              objectFit: 'contain',
            }}
          />
        </Box>

        {isAuthenticated && username && (
          <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <ThemeProvider theme={theme1}>
                {/* Settings icon for admin only */}

                <IconButton
                  size="large"
                  edge="end"
                  aria-label="Open user menu"
                  aria-controls={menuId}
                  aria-haspopup="true"
                  aria-expanded={Boolean(anchorEl)}
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                >
                  <Tooltip
                    title={username}
                    placement="top"
                    PopperProps={{
                      modifiers: [
                        {
                          name: "preventOverflow",
                          options: { boundary: "window" },
                        },
                        {
                          name: "offset",
                          options: { offset: [0, -10] },
                        },
                      ],
                      style: {
                        zIndex: 9999,
                      }
                    }}
                    sx={{
                      zIndex: 9999,
                      '& .MuiTooltip-tooltip': {
                        zIndex: 9999,
                      }
                    }}
                  >
                    <Avatar {...stringAvatar(username)} />
                  </Tooltip>
                </IconButton>

              </ThemeProvider>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="small" // Changed from "large" to "small"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="inherit"
                sx={{ padding: { xs: '4px', sm: '8px' } }} // Reduced padding on extra small screens
              >
                <Avatar {...stringAvatar(username)} fontSize="medium" />

              </IconButton>
            </Box>
          </Box>
        )}
      </Toolbar>
      {renderMobileMenu}
      {renderMenu}
    </AppBar>
  );
};

export default React.memo(Navbar);